import React, {useEffect, useRef, useState} from "react";
import {Box} from "@mui/material";
import {useTheme} from "../../contexts/theme";
import {Landmark, TrimPoint, Grabber, FocusElement} from "../../types/editor";
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import LandMarkSVG from "./LandMarkSVG";
import {moveTrimPoint} from "../../utils/utils"

interface VideoPlayerBarProps {
    trimPoints: TrimPoint[];
    landmarks: Landmark[];
    videoCurrentTime: number;
    totalVideoTime: number;
    updateTrimPoint: (trimPoints: TrimPoint[], time: number) => void;
    setVideoCurrentDuration: (currentTime: number) => void;
    deleteMode: boolean;
    onDeleteGrabber: (grabberIndex: number) => void;
    onLandmarkClick: (landmarkIndex: number) => void;
    setIsGrabberDragging: (isGrabberDragging: boolean) => void;
    setCurrentFocus: (focusElement: FocusElement) => void;
    onBlur: () => void;
    mergeTrimPoints: (index1 :number, index2: number) => void
    shots: number[]
}

const landMarkStyle = {
    position: "absolute",
    top: "-25px",
    bottom: "-22.5px",
    width: "9px",
    border: "none",
    zIndex: 15,
    cursor: "pointer",
    transition: "transform 0.2s ease",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "transparent"
}


function StartGrabber(props: any) {
    return (
        <PlayArrowIcon tabIndex='0' {...props} style={{transform: 'rotate(90deg)'}}/>
    );
}

function EndGrabber(props: any) {
    return (
        <PlayArrowIcon tabIndex='0' {...props} style={{transform: 'rotate(-90deg)'}}/>
    );
}

interface LandmarkCompProps {
    landmark: Landmark;
    index: number;
}

interface PlayerBarProps {
    left: string;
}

const barHeight = 10


const VideoPlayerBar: React.FC<VideoPlayerBarProps> = ({
                                                           deleteMode,
                                                           onDeleteGrabber,
                                                           setVideoCurrentDuration,
                                                           updateTrimPoint,
                                                           videoCurrentTime,
                                                           totalVideoTime,
                                                           trimPoints,
                                                           landmarks,
                                                           onLandmarkClick,
                                                           setIsGrabberDragging,
                                                           setCurrentFocus,
                                                           onBlur,
                                                           mergeTrimPoints,
                                                            shots
                                                       }) => {

    function LandMark({landmark, index}: LandmarkCompProps) {
        return (
            <Box>
                { barRef.current && <Box position='absolute' sx={{...landMarkStyle, left: (landmark.offset * barRef.current!.clientWidth) - 3 }} onClick={(e) => handleLandmarkClick(e, index)}>
                    <LandMarkSVG color={theme.palette.lightGrey}/>
                </Box>}
                { !barRef.current && <></> }
            </Box>
        )
    }

    function VideoPlayerBar({left}: PlayerBarProps ){
        return (
            <Box
                width={'2px'}
                height={(barHeight + 10) + 'px'}
                sx={{backgroundColor: theme.palette.black, zIndex: 10}}
                position='absolute'
                left={left}
                tabIndex={0}
                onFocus={() => setCurrentFocus({type: 'startGrabber', index: -1})}
                onBlur={onBlur}
            />
        )
    }

    const theme = useTheme();
    const barRef = useRef<HTMLDivElement | null>(null);
    const selectedGrabber = useRef<Grabber | undefined>();
    const [gradientBackGround, setGradientBackGround] = useState<string>();

    function calculateBackground() {
        if (trimPoints.length > 0) {
            let colors = '';
            let counter = 0;
            const activeColor = theme.palette.red;
            const deactiveColor = theme.palette.lightGrey;
            colors += `, ${activeColor} 0%, ${activeColor} ${trimPoints[0].start / totalVideoTime * 100}%`
            for (let trimPoint of trimPoints) {
                if (counter > 0) {
                    colors += `, ${activeColor} ${trimPoints[counter - 1].end / totalVideoTime * 100}%, ${activeColor} ${trimPoint.start / totalVideoTime * 100}%`
                }
                colors += `, ${deactiveColor} ${trimPoint.start / totalVideoTime * 100}%, ${deactiveColor} ${trimPoint.end / totalVideoTime * 100}%`
                counter += 1
            }
            colors += `, ${activeColor} ${trimPoints[counter - 1].end / totalVideoTime * 100}%, ${activeColor} 100%`
            setGradientBackGround(`linear-gradient(to right ${colors})`);
        } else {
            setGradientBackGround(theme.palette.lightGrey)
        }
    }

    function getLeftOffset(time: number) {
        if (barRef.current) {
            const currentWidth = barRef.current.clientWidth;
            return ((time / totalVideoTime) * currentWidth)
        } else {
            return 0;
        }
    }

    function onGrabEnd() {
        window.removeEventListener('mousemove', onGrabMove);
        window.removeEventListener('mouseup', onGrabEnd);
        setIsGrabberDragging(false);
    }


    function onGrabMove(event: MouseEvent) {
        const playbackRect = barRef.current!.getBoundingClientRect();
        const seekRatio = (event.clientX - playbackRect.left) / playbackRect.width;
        const index = selectedGrabber.current!.index;
        const type = selectedGrabber.current!.type;
        const seek = totalVideoTime * seekRatio
        moveTrimPoint(trimPoints, seek, totalVideoTime, index, type, onDeleteGrabber, updateTrimPoint, mergeTrimPoints);
    }

    function onGrabberSelection(grabberIndex: number, type: 'start' | 'end', time: number) {
        if (deleteMode) {
            onDeleteGrabber(grabberIndex)
        } else {
            setIsGrabberDragging(true);
            selectedGrabber.current = {index: grabberIndex, type, time};
            setVideoCurrentDuration(time)
            window.addEventListener('mousemove', onGrabMove);
            window.addEventListener('mouseup', onGrabEnd);
        }
    }

    function onBarClick(e: any) {
        if (barRef && barRef.current) {
            const playbackRect = barRef.current.getBoundingClientRect();
            const seekTime = ((e.clientX - playbackRect.left) / playbackRect.width) * totalVideoTime;
            setVideoCurrentDuration(seekTime)
        }
    }

    function handleLandmarkClick(e: any, landmarkIndex: number){
        e.stopPropagation();
        const heightDeleteThreshold = 20;
        const containerRect = e.target!.parentNode.getBoundingClientRect();
        const offsetY = e.clientY - containerRect.top;
        if (offsetY < heightDeleteThreshold){
            onLandmarkClick(landmarkIndex);
        }else if (offsetY > heightDeleteThreshold){
            setVideoCurrentDuration(landmarks[landmarkIndex].time);
        }
    }

    useEffect(() => {
        calculateBackground();
    }, [trimPoints, totalVideoTime]);

    return (
        <Box ref={barRef} onClick={onBarClick} position={'relative'} height={barHeight + 'px'}
             sx={{background: gradientBackGround, borderRadius: theme.border.radius.md}}>
            {trimPoints.map((trimPoint, index) => (
                <React.Fragment key={'Trimpoints' + index}>
                    <StartGrabber
                        onMouseDown={(e: any) => onGrabberSelection(index, 'start', trimPoint.start)}
                        onFocus={() => setCurrentFocus({type: 'startGrabber', index})}
                        onBlur={onBlur}
                        onClick={(e: any) => e.stopPropagation()}
                        sx={{
                            position: 'absolute',
                            left: (getLeftOffset(trimPoint.start) - 12) + 'px',
                            color: 'green',
                            top: -(barHeight + 10) + 'px',
                            tabIndex: 0
                        }}></StartGrabber>
                    <EndGrabber
                        onMouseDown={(e: any) => onGrabberSelection(index, 'end', trimPoint.end)}
                        onFocus={() => setCurrentFocus({type: 'endGrabber', index})}
                        onClick={(e: any) => e.stopPropagation()}
                        onBlur={onBlur}
                        sx={{
                            position: 'absolute',
                            left: (getLeftOffset(trimPoint.end) - 12) + 'px',
                            color: 'red',
                            top: (barHeight - 2) + 'px',
                            tabIndex: 0
                        }}></EndGrabber>
                </React.Fragment>
            ))}
            {landmarks.map((landmark, index) => (
                <LandMark landmark={landmark} index={index} key={'landmark-' + index}/>
            ))}
            {shots.map((shot, index) => (
                <VideoPlayerBar left={getLeftOffset(shot) + 'px'} key={'shot' + index}/>
            ))}
            <VideoPlayerBar left={getLeftOffset(videoCurrentTime) + 'px'}/>
        </Box>
    );
};

export default VideoPlayerBar;